import managerChatAPI from '../../api/managerChat';

export const types = {
  SET_THREAD: 'SET_MANAGER_CHAT_THREAD',
  SET_MESSAGES: 'SET_MANAGER_CHAT_MESSAGES',
  APPEND_MESSAGE: 'APPEND_MANAGER_CHAT_MESSAGE',
  REMOVE_MESSAGE: 'REMOVE_MANAGER_CHAT_MESSAGE',
  SET_AI_TYPING: 'SET_MANAGER_CHAT_AI_TYPING',
  SET_AI_TIMED_OUT: 'SET_MANAGER_CHAT_AI_TIMED_OUT',
  SET_AI_PROGRESS: 'SET_MANAGER_CHAT_AI_PROGRESS',
  APPEND_AI_PROGRESS: 'APPEND_MANAGER_CHAT_AI_PROGRESS',
  RESET_AI_PROGRESS_LOG: 'RESET_MANAGER_CHAT_AI_PROGRESS_LOG',
  SET_UI_FLAG: 'SET_MANAGER_CHAT_UI_FLAG',
  CLEAR: 'CLEAR_MANAGER_CHAT',
};

const AI_SOFT_WARNING_MS = 90 * 1000; // показать «всё ещё думает» — спиннер продолжает крутиться
const AI_HARD_TIMEOUT_MS = 600 * 1000; // 10 мин — реальная ошибка
const MAX_PROGRESS_LOG = 20; // сколько последних стадий «хода мыслей» держим в логе
const typingTimers = {}; // { [conversationId]: { soft, hard } }

function clearTypingTimers(conversationId) {
  const t = typingTimers[conversationId];
  if (!t) return;
  if (t.soft) clearTimeout(t.soft);
  if (t.hard) clearTimeout(t.hard);
  delete typingTimers[conversationId];
}

function startTypingTimers(commit, conversationId) {
  clearTypingTimers(conversationId);
  typingTimers[conversationId] = {
    soft: setTimeout(() => {
      // Spinner продолжает крутиться, просто появляется мягкий баннер.
      commit(types.SET_AI_TIMED_OUT, { conversationId, value: 'soft' });
    }, AI_SOFT_WARNING_MS),
    hard: setTimeout(() => {
      commit(types.SET_AI_TYPING, { conversationId, value: false });
      commit(types.SET_AI_TIMED_OUT, { conversationId, value: 'hard' });
      commit(types.SET_AI_PROGRESS, { conversationId, value: null });
      commit(types.RESET_AI_PROGRESS_LOG, { conversationId });
      delete typingTimers[conversationId];
    }, AI_HARD_TIMEOUT_MS),
  };
}

const state = {
  // conversationId -> { thread, messages: [] }
  byConversation: {},
  // conversationId -> bool (AI печатает...)
  aiTyping: {},
  // conversationId -> bool (AI не ответил за таймаут)
  aiTimedOut: {},
  // conversationId -> { stage, message } — текущий (последний) progress-тик от AI-back
  aiProgress: {},
  // conversationId -> [{ stage, message, at }] — лог стадий «хода мыслей» AI
  aiProgressLog: {},
  // conversationId -> epoch ms последнего progress-тика (для счётчика свежести)
  aiProgressAt: {},
  uiFlags: {
    fetching: false,
    submitting: false,
  },
};

export const getters = {
  getThread: _state => conversationId => _state.byConversation[conversationId]?.thread || null,
  getMessages: _state => conversationId => _state.byConversation[conversationId]?.messages || [],
  getLatestPendingDraft: _state => conversationId => {
    const bucket = _state.byConversation[conversationId];
    if (!bucket || bucket.thread?.status !== 'open') return null;
    for (let i = bucket.messages.length - 1; i >= 0; i -= 1) {
      const m = bucket.messages[i];
      if (m.role === 'manager') return null;
      if (m.role === 'ai' && m.kind === 'draft_proposal') return m;
    }
    return null;
  },
  isAiTyping: _state => conversationId => Boolean(_state.aiTyping[conversationId]),
  isAiTimedOut: _state => conversationId => Boolean(_state.aiTimedOut[conversationId]),
  getAiTimeoutLevel: _state => conversationId => _state.aiTimedOut[conversationId] || null,
  getAiProgress: _state => conversationId => _state.aiProgress[conversationId] || null,
  getAiProgressLog: _state => conversationId => _state.aiProgressLog[conversationId] || [],
  getAiProgressAt: _state => conversationId => _state.aiProgressAt[conversationId] || null,
  hasOpenThread: _state => conversationId => _state.byConversation[conversationId]?.thread?.status === 'open',
  hasPendingDraft: _state => conversationId => Boolean(_state.byConversation[conversationId]?.thread?.has_pending_draft),
  getUIFlags: _state => _state.uiFlags,
};

export const actions = {
  async fetch({ commit }, conversationId) {
    if (!conversationId) return null;
    commit(types.SET_UI_FLAG, { fetching: true });
    try {
      const { data } = await managerChatAPI.show(conversationId);
      commit(types.SET_THREAD, { conversationId, thread: data.thread, messages: data.messages || [] });
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { fetching: false });
    }
  },

  async turn({ commit, dispatch }, { conversationId, prompt = null, images = [] }) {
    commit(types.SET_UI_FLAG, { submitting: true });
    try {
      await managerChatAPI.turn(conversationId, { prompt, images });
      commit(types.SET_AI_TIMED_OUT, { conversationId, value: null });
      commit(types.SET_AI_PROGRESS, { conversationId, value: null });
      commit(types.RESET_AI_PROGRESS_LOG, { conversationId });
      commit(types.SET_AI_TYPING, { conversationId, value: true });
      // 90с → мягкий warning «всё ещё думает» (спиннер крутится дальше).
      // 600с → hard timeout: спиннер останавливается, баннер ошибки.
      startTypingTimers(commit, conversationId);
      // Перечитаем thread — там теперь будет manager-сообщение (если был prompt).
      await dispatch('fetch', conversationId);
    } finally {
      commit(types.SET_UI_FLAG, { submitting: false });
    }
  },

  async accept({ commit, dispatch }, { conversationId, turnId, editedContent = null }) {
    commit(types.SET_UI_FLAG, { submitting: true });
    try {
      const { data } = await managerChatAPI.accept(conversationId, { turnId, editedContent });
      await dispatch('fetch', conversationId);
      clearTypingTimers(conversationId);
      commit(types.SET_AI_TYPING, { conversationId, value: false });
      commit(types.SET_AI_TIMED_OUT, { conversationId, value: null });
      commit(types.SET_AI_PROGRESS, { conversationId, value: null });
      commit(types.RESET_AI_PROGRESS_LOG, { conversationId });
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { submitting: false });
    }
  },

  async dismiss({ commit, dispatch }, conversationId) {
    commit(types.SET_UI_FLAG, { submitting: true });
    try {
      const { data } = await managerChatAPI.dismiss(conversationId);
      await dispatch('fetch', conversationId);
      clearTypingTimers(conversationId);
      commit(types.SET_AI_TYPING, { conversationId, value: false });
      commit(types.SET_AI_TIMED_OUT, { conversationId, value: null });
      commit(types.SET_AI_PROGRESS, { conversationId, value: null });
      commit(types.RESET_AI_PROGRESS_LOG, { conversationId });
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { submitting: false });
    }
  },

  clearAiTimeout({ commit }, conversationId) {
    commit(types.SET_AI_TIMED_OUT, { conversationId, value: null });
  },

  // ActionCable: 'manager_chat.progress' → обновляем тик и продлеваем оба таймера.
  // Раз AI прислал прогресс — он жив. Перезапускаем 90с/600с от этого момента
  // и снимаем soft-warning (если был).
  receiveProgress({ commit, state: _state }, payload) {
    if (!payload?.conversation_id) return;
    const conversationId = payload.conversation_id;
    const tick = {
      stage: payload.stage || null,
      message: payload.message || null,
      at: Date.now(),
    };
    commit(types.SET_AI_PROGRESS, { conversationId, value: tick });
    // Пустые тики (без stage и message) — это «пульс жизни»: лог не засоряем,
    // но таймеры свежести всё равно продлеваем ниже.
    if (tick.stage || tick.message) {
      commit(types.APPEND_AI_PROGRESS, { conversationId, value: tick });
    }
    if (_state.aiTyping[conversationId]) {
      commit(types.SET_AI_TIMED_OUT, { conversationId, value: null });
      startTypingTimers(commit, conversationId);
    }
  },

  // ActionCable: 'manager_chat.turn_failed' → доставка turn'а на AI-бэкенд
  // окончательно провалилась (таймаут/ошибка после всех ретраев). Гасим спиннер
  // и показываем ошибку, чтобы менеджер не ждал впустую.
  receiveTurnFailed({ commit }, payload) {
    if (!payload?.conversation_id) return;
    const conversationId = payload.conversation_id;
    clearTypingTimers(conversationId);
    commit(types.SET_AI_TYPING, { conversationId, value: false });
    commit(types.SET_AI_PROGRESS, { conversationId, value: null });
    commit(types.RESET_AI_PROGRESS_LOG, { conversationId });
    commit(types.SET_AI_TIMED_OUT, { conversationId, value: 'failed' });
  },

  async deleteMessage({ commit }, { conversationId, messageId }) {
    // Оптимистичное удаление: сразу прячем из списка, на ошибке откатываем через fetch.
    commit(types.REMOVE_MESSAGE, { conversationId, messageId });
    try {
      await managerChatAPI.deleteMessage(conversationId, messageId);
    } catch (err) {
      // Не получилось — перечитаем thread, чтобы вернуть актуальное состояние.
      await this.dispatch('managerChat/fetch', conversationId);
      throw err;
    }
  },

  async rememberMessage(_ctx, { conversationId, messageId }) {
    // Backend шлёт webhook, AI-back обрабатывает асинхронно и возвращает
    // memory_saved через ai_message → ActionCable. UI просто ждёт его прилёта.
    await managerChatAPI.rememberMessage(conversationId, messageId);
  },

  // ActionCable: 'manager_chat.message.deleted' → удаляем сообщение из state.
  receiveDeleted({ commit, state: _state }, payload) {
    if (!payload?.conversation_id || !payload?.message_id) return;
    const conversationId = payload.conversation_id;
    const bucket = _state.byConversation[conversationId];
    if (!bucket) return;
    commit(types.REMOVE_MESSAGE, { conversationId, messageId: payload.message_id });
    if (payload.thread) {
      commit(types.SET_THREAD, {
        conversationId,
        thread: { ...bucket.thread, ...payload.thread },
        messages: undefined,
      });
    }
  },

  // ActionCable: 'manager_chat.message.created' → пушим новое сообщение в state.
  receive({ commit, state: _state }, payload) {
    if (!payload?.conversation_id) return;
    const conversationId = payload.conversation_id;
    const message = payload.message;
    if (!message) return;

    const bucket = _state.byConversation[conversationId];
    // Если ещё не загружали thread — игнорируем (откроется позже через fetch).
    if (!bucket) return;

    const exists = bucket.messages.some(m => m.id === message.id);
    if (!exists) commit(types.APPEND_MESSAGE, { conversationId, message });

    // Обновляем флаги thread'а (has_pending_draft), если пришёл апдейт.
    if (payload.thread) {
      commit(types.SET_THREAD, {
        conversationId,
        thread: { ...bucket.thread, ...payload.thread },
        messages: undefined, // не трогаем messages
      });
    }
    // AI прислал сообщение — гасим спиннер, очищаем баннер любого уровня.
    // Это покрывает кейс «AI ответил после 10-минутного hard-timeout»:
    // hard-баннер исчезнет автоматически, ответ отрендерится.
    if (message.role === 'ai') {
      clearTypingTimers(conversationId);
      commit(types.SET_AI_TYPING, { conversationId, value: false });
      commit(types.SET_AI_TIMED_OUT, { conversationId, value: null });
      commit(types.SET_AI_PROGRESS, { conversationId, value: null });
      commit(types.RESET_AI_PROGRESS_LOG, { conversationId });
    }
  },

  setAiTyping({ commit }, payload) {
    commit(types.SET_AI_TYPING, payload);
  },
};

export const mutations = {
  [types.SET_THREAD](_state, { conversationId, thread, messages }) {
    const prev = _state.byConversation[conversationId] || { thread: null, messages: [] };
    _state.byConversation = {
      ..._state.byConversation,
      [conversationId]: {
        thread: thread || null,
        messages: messages !== undefined ? messages : prev.messages,
      },
    };
  },
  [types.SET_MESSAGES](_state, { conversationId, messages }) {
    const prev = _state.byConversation[conversationId] || { thread: null, messages: [] };
    _state.byConversation = {
      ..._state.byConversation,
      [conversationId]: { ...prev, messages: messages || [] },
    };
  },
  [types.APPEND_MESSAGE](_state, { conversationId, message }) {
    const prev = _state.byConversation[conversationId] || { thread: null, messages: [] };
    _state.byConversation = {
      ..._state.byConversation,
      [conversationId]: { ...prev, messages: [...prev.messages, message] },
    };
  },
  [types.REMOVE_MESSAGE](_state, { conversationId, messageId }) {
    const prev = _state.byConversation[conversationId];
    if (!prev) return;
    _state.byConversation = {
      ..._state.byConversation,
      [conversationId]: { ...prev, messages: prev.messages.filter(m => m.id !== messageId) },
    };
  },
  [types.SET_AI_TYPING](_state, { conversationId, value }) {
    _state.aiTyping = { ..._state.aiTyping, [conversationId]: Boolean(value) };
  },
  [types.SET_AI_TIMED_OUT](_state, { conversationId, value }) {
    // value: 'soft' | 'hard' | null
    _state.aiTimedOut = { ..._state.aiTimedOut, [conversationId]: value || null };
  },
  [types.SET_AI_PROGRESS](_state, { conversationId, value }) {
    _state.aiProgress = { ..._state.aiProgress, [conversationId]: value || null };
    _state.aiProgressAt = {
      ..._state.aiProgressAt,
      [conversationId]: value ? Date.now() : null,
    };
  },
  [types.APPEND_AI_PROGRESS](_state, { conversationId, value }) {
    const prev = _state.aiProgressLog[conversationId] || [];
    const next = [...prev, value].slice(-MAX_PROGRESS_LOG);
    _state.aiProgressLog = { ..._state.aiProgressLog, [conversationId]: next };
  },
  [types.RESET_AI_PROGRESS_LOG](_state, { conversationId }) {
    _state.aiProgressLog = { ..._state.aiProgressLog, [conversationId]: [] };
    _state.aiProgressAt = { ..._state.aiProgressAt, [conversationId]: null };
  },
  [types.SET_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.CLEAR](_state, conversationId) {
    const next = { ..._state.byConversation };
    delete next[conversationId];
    _state.byConversation = next;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
